import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

type DatePickerProps = {
  name?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onCommit?: () => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
};

const monthFormatter = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });
const dayFormatter = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
const shortFormatter = new Intl.DateTimeFormat("pt-BR");
const weekDays = ["D", "S", "T", "Q", "Q", "S", "S"];

function formatMonth(date: Date) {
  const label = monthFormatter.format(date);
  return label.charAt(0).toLocaleUpperCase("pt-BR") + label.slice(1);
}

function parseDate(value?: string) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day, 12);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function toValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function sameDate(first?: Date, second?: Date) {
  return Boolean(first && second && toValue(first) === toValue(second));
}

export function DatePicker({
  name,
  value,
  defaultValue = "",
  onValueChange,
  onCommit,
  min,
  max,
  required,
  disabled,
  className = "",
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const controlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = controlled ? value : internalValue;
  const selected = parseDate(currentValue);
  const [visibleMonth, setVisibleMonth] = useState(() => selected || new Date());
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) setVisibleMonth(selected);
  }, [currentValue]);

  const placePopover = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = 292;
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
    const preferredTop = rect.bottom + 7;
    const top = preferredTop + 344 < window.innerHeight ? preferredTop : Math.max(12, rect.top - 344);
    setPosition({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    placePopover();
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("resize", placePopover);
    window.addEventListener("scroll", placePopover, true);
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", placePopover);
      window.removeEventListener("scroll", placePopover, true);
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const days = useMemo(() => {
    const first = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1, 12);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const choose = (nextValue: string) => {
    if (!controlled) setInternalValue(nextValue);
    onValueChange?.(nextValue);
    setOpen(false);
    window.requestAnimationFrame(() => {triggerRef.current?.focus();onCommit?.()});
  };

  const today = new Date();
  const minimum = parseDate(min);
  const maximum = parseDate(max);

  return (
    <div className={`sgq-date-picker ${className}`.trim()}>
      {name && <input type="hidden" name={name} value={currentValue || ""} />}
      {required && <input
        className="sgq-date-required-proxy"
        type="text"
        value={currentValue || ""}
        required
        tabIndex={-1}
        aria-hidden="true"
        onChange={() => undefined}
        onInvalid={(event) => {
          event.preventDefault();
          setOpen(true);
          window.requestAnimationFrame(() => triggerRef.current?.focus());
        }}
      />}
      <button
        ref={triggerRef}
        type="button"
        className="sgq-date-trigger"
        disabled={disabled}
        aria-label={ariaLabel || "Selecionar data"}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) placePopover();
          setOpen((current) => !current);
        }}
      >
        <span className={selected ? "" : "is-placeholder"}>{selected ? shortFormatter.format(selected) : "Selecionar data"}</span>
        <CalendarDays aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          ref={popoverRef}
          className="sgq-date-popover"
          role="dialog"
          aria-label="Calendário"
          style={{ top: position.top, left: position.left }}
        >
          <header>
            <strong>{formatMonth(visibleMonth)}</strong>
            <div>
              <button type="button" aria-label="Mês anterior" onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}><ChevronLeft /></button>
              <button type="button" aria-label="Próximo mês" onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}><ChevronRight /></button>
            </div>
          </header>
          <div className="sgq-date-weekdays" aria-hidden="true">{weekDays.map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}</div>
          <div className="sgq-date-grid">
            {days.map((date) => {
              const dateValue = toValue(date);
              const outside = date.getMonth() !== visibleMonth.getMonth();
              const unavailable = Boolean((minimum && date < minimum) || (maximum && date > maximum));
              return <button
                type="button"
                key={dateValue}
                className={`${outside ? "is-outside" : ""} ${sameDate(date, selected) ? "is-selected" : ""} ${sameDate(date, today) ? "is-today" : ""}`.trim()}
                disabled={unavailable}
                aria-label={dayFormatter.format(date)}
                aria-pressed={sameDate(date, selected)}
                onClick={() => choose(dateValue)}
              >{date.getDate()}</button>;
            })}
          </div>
          <footer>
            {!required && <button type="button" className="sgq-date-clear" onClick={() => choose("")}>Limpar</button>}
            <button type="button" className="sgq-date-today" onClick={() => choose(toValue(today))}>Hoje</button>
          </footer>
        </div>,
        document.body,
      )}
    </div>
  );
}
